<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";

import { ref, computed } from "vue";
import { useRoute } from "vue-router";

import VoltadminLogo from "@/assets/logos/voltalis-blue-icon.svg?url";
import { useTypedIntl } from "@/locales/typed-intl";
import { ERouteName } from "@/router/router.type";

import Breadcrumb from "primevue/breadcrumb";

export type TCustomBreadcrumbRouteFormatter = Partial<Record<ERouteName, (item: MenuItem) => string>>;

interface IProps {
  formatter?: TCustomBreadcrumbRouteFormatter;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();
const route = useRoute();

const home = ref({
  img: VoltadminLogo,
  name: ERouteName.HOME,
});

const breadcrumb = computed(() => {
  return route.matched;
});

function formatRouteItem(item: MenuItem): string {
  const name = item.name as ERouteName;
  if (props.formatter) {
    const maybeCustomFormatter = props.formatter[name];

    if (maybeCustomFormatter) {
      return maybeCustomFormatter(item);
    }
  }

  return t(k.components.layout.breadcrumb[name]);
}
</script>

<template>
  <Breadcrumb
    class="breadcrumb"
    :home="home"
    :model="breadcrumb"
  >
    <template #item="{ item }">
      <router-link
        class="cursor-pointer"
        :to="{ name: item.name }"
      >
        <img
          v-if="item.img"
          :src="item.img"
        />
        <span
          v-else
          class="text-text-secondary"
        >
          {{ formatRouteItem(item) }}
        </span>
      </router-link>
    </template>

    <template #separator>
      <i class="pi pi-angle-right" />
    </template>
  </Breadcrumb>
</template>

<style lang="scss">
.breadcrumb.p-breadcrumb {
  @apply flex items-center;
  @apply bg-transparent;
  @apply border-none;
  @apply py-2 px-0;

  .p-breadcrumb-list > .p-menuitem-separator > svg {
    @apply h-[10px];
    @apply text-text-secondary;
  }

  .router-link-exact-active > span {
    @apply font-medium text-text-primary;
  }
}
</style>
